/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { inject, Injectable } from '@angular/core';
import { AbstractResourceService, JsonApiListResourceMetadataBase, JsonApiResource } from '@irian-ro/abstract-resource';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from '@irian-ro/util-environment';
import {
  ETransportNotificationDetailsMetadataModel,
  ETransportNotificationDTO,
  ETransportNotificationMetadata,
  ETransportNotificationVO,
} from '@well/delivery-libs/etransport/domain-model';
import { Observable } from 'rxjs';
import { EmailablePayloadWithContactsMapDTO } from '@well/domain';
import { ETransportDataAccessUtilService } from './e-transport-data-access-util.service';

@Injectable({
  providedIn: 'root',
})
export class ETransportNotificationResourceService extends AbstractResourceService<
  ETransportNotificationDTO,
  ETransportNotificationDTO,
  ETransportNotificationVO,
  ETransportNotificationVO
> {
  constructor(httpClient: HttpClient, environmentService: EnvironmentService) {
    super(
      httpClient,
      environmentService.getEnvironmentProperty('eTransportNotificationUrl'),
      ETransportNotificationDTO,
      ETransportNotificationDTO,
      ETransportNotificationVO,
      ETransportNotificationVO
    );
  }

  eTransportDataAccessUtilService = inject(ETransportDataAccessUtilService);

  saveEmailable(
    eTransportNotificationWithContacts: EmailablePayloadWithContactsMapDTO<ETransportNotificationDTO>
  ): Observable<JsonApiResource<ETransportNotificationDTO, null>> {
    const notification = this.eTransportDataAccessUtilService.processAddresses(eTransportNotificationWithContacts);

    return <Observable<JsonApiResource<ETransportNotificationDTO, null>>>(
      this.httpClient?.post(`${this.url}`, notification)
    );
  }

  updateEmailable(
    eTransportNotificationWithContacts: EmailablePayloadWithContactsMapDTO<ETransportNotificationDTO>
  ): Observable<JsonApiResource<ETransportNotificationDTO, null>> {
    const notification = this.eTransportDataAccessUtilService.processAddresses(eTransportNotificationWithContacts);

    return <Observable<JsonApiResource<ETransportNotificationDTO, null>>>(
      this.httpClient?.put(`${this.url}`, notification)
    );
  }

  getResource(
    uuid: string
  ): Observable<JsonApiResource<ETransportNotificationDTO, ETransportNotificationDetailsMetadataModel>> {
    return this.httpClient?.get(`${this.url}/${uuid}`).pipe(this.getDtoMapper());
  }

  getTemplateFromShipment(
    shipmentUuid: string
  ): Observable<JsonApiResource<ETransportNotificationDTO, ETransportNotificationMetadata>> {
    return this.httpClient?.get(`${this.url}/template/shipment/${shipmentUuid}`).pipe(this.getDtoMapper());
  }

  upload(uuid: string): Observable<any> {
    return this.httpClient?.post(`${this.url}/upload/${uuid}`, {}).pipe(this.getDtoMapper());
  }

  getNotificationSummary(shipmentUuid: string) {
    return this.httpClient?.get<JsonApiResource<ETransportNotificationVO[], JsonApiListResourceMetadataBase>>(
      `${this.url}/summary/${shipmentUuid}`,
      {}
    );
  }

  getTemplateFromDuplicatedNotification(notificationUuid: string) {
    return this.httpClient?.get(`${this.url}/template/duplicate/${notificationUuid}`).pipe(this.getDtoMapper());
  }
}
